/**
 * Project name/subtitle/description are free-text on the backend `projects`
 * table, but admins store per-locale overrides inside the freeform `meta`
 * JSONB (`name_<locale>`, `subtitle_<locale>`, `description_html_<locale>`).
 * These helpers read the active locale's override, falling back to the legacy
 * unsuffixed keys and finally the raw column — so untranslated projects keep
 * rendering exactly as before.
 */

type ProjectMeta = Record<string, unknown> | null | undefined;

const metaStr = (meta: ProjectMeta, key: string): string =>
  typeof meta?.[key] === "string" ? (meta[key] as string).trim() : "";

/** Localized project name: `meta.name_<locale>` → raw `name`. */
export const localizedProjectName = (
  meta: ProjectMeta,
  fallbackName: string | null | undefined,
  locale: string,
): string => metaStr(meta, `name_${locale}`) || (fallbackName ?? "").trim();

/** Localized homepage tagline: locale subtitle → legacy subtitle/description → category. */
export const localizedProjectSubtitle = (
  meta: ProjectMeta,
  category: string | null | undefined,
  locale: string,
): string | null =>
  metaStr(meta, `subtitle_${locale}`) ||
  metaStr(meta, "subtitle") ||
  metaStr(meta, `description_${locale}`) ||
  metaStr(meta, "description") ||
  category?.trim() ||
  null;

/** Localized detail-page description HTML: `meta.description_html_<locale>` → raw `description`. */
export const localizedProjectDescriptionHtml = (
  meta: ProjectMeta,
  fallbackDescription: string | null | undefined,
  locale: string,
): string =>
  metaStr(meta, `description_html_${locale}`) || (fallbackDescription ?? "");

/**
 * Per-slot banner URL: `meta.banner_<slot>_url` → the legacy `banner_url`
 * column. `home` feeds the home featured banner, `checkout` the fast-checkout
 * detail hero; both fall back to the column so un-migrated projects keep
 * rendering. Empty when neither resolves (caller hides the banner).
 */
export const projectBannerUrl = (
  meta: ProjectMeta,
  fallbackColumn: string | null | undefined,
  slot: "home" | "checkout",
): string =>
  metaStr(meta, `banner_${slot}_url`) || (fallbackColumn ?? "").trim();

export interface FeaturedProject {
  id: string;
  href: string;
  bannerUrl: string;
  name: string;
}

/** Minimal project shape the home banner needs (subset of ProjectResponseDto). */
interface FeaturableProject {
  id: string;
  name: string | null;
  banner_url: string;
  is_active: boolean | null;
  meta: ProjectMeta;
}

/** `meta.is_featured` may be a JSONB boolean or string — treat both as truthy. */
export const isFeaturedMeta = (meta: ProjectMeta): boolean => {
  const v = (meta as Record<string, unknown> | null | undefined)?.is_featured;
  return v === true || v === "true" || v === 1 || v === "1";
};

/**
 * The single active, featured project for the home banner — or null when none
 * is flagged (so the banner hides). Admins flip `is_active`/`meta.is_featured`
 * to control it in prod without a deploy.
 */
export const selectFeaturedProject = (
  projects: ReadonlyArray<FeaturableProject>,
  locale: string,
): FeaturedProject | null => {
  const found = projects.find(
    (p) =>
      p.is_active &&
      isFeaturedMeta(p.meta) &&
      projectBannerUrl(p.meta, p.banner_url, "home"),
  );
  if (!found) return null;
  return {
    id: found.id,
    href: `/fast-checkout/${found.id}`,
    bannerUrl: projectBannerUrl(found.meta, found.banner_url, "home"),
    name: localizedProjectName(found.meta, found.name, locale),
  };
};
